import type { IAgentRuntime } from "@elizaos/core";
import { z } from "zod";

// Environment Variables
let ENV = "mainnet";

// Pyth Network Configuration
const PYTH_NETWORKS = {
  mainnet: {
    hermes:
      process.env.PYTH_MAINNET_HERMES_URL || "https://hermes.pyth.network",
    wss: process.env.PYTH_MAINNET_WSS_URL || "wss://hermes.pyth.network/ws",
    pythnet:
      process.env.PYTH_MAINNET_PYTHNET_URL || "https://pythnet.rpcpool.com",
    contractRegistry:
      process.env.PYTH_MAINNET_CONTRACT_REGISTRY ||
      "https://pyth.network/developers/price-feed-ids",
    programKey:
      process.env.PYTH_MAINNET_PROGRAM_KEY ||
      "FsJ3A3u2vn5cTVofAjvy6y5kwABJAqYWpe4975bi2epH",
  },
  testnet: {
    hermes:
      process.env.PYTH_TESTNET_HERMES_URL || "https://hermes-beta.pyth.network",
    wss:
      process.env.PYTH_TESTNET_WSS_URL || "wss://hermes-beta.pyth.network/ws",
    pythnet:
      process.env.PYTH_TESTNET_PYTHNET_URL || "https://api.devnet.solana.com",
    contractRegistry:
      process.env.PYTH_TESTNET_CONTRACT_REGISTRY ||
      "https://pyth.network/developers/price-feed-ids#testnet",
    programKey:
      process.env.PYTH_TESTNET_PROGRAM_KEY ||
      "FsJ3A3u2vn5cTVofAjvy6y5kwABJAqYWpe4975bi2epH",
  },
} as const;

// // Log environment information
// elizaLogger.info("Environment sources", {
//     shellVars: Object.keys(process.env).filter(key => key.startsWith('PYTH_')),
// });

export const pythEnvSchema = z.object({
  PYTH_NETWORK_ENV: z.enum(["mainnet", "testnet"]).default("mainnet"),
  PYTH_MAX_RETRIES: z.string().transform(Number).default("3"),
  PYTH_RETRY_DELAY: z.string().transform(Number).default("1000"),
  PYTH_TIMEOUT: z.string().transform(Number).default("5000"),
  PYTH_GRANULAR_LOG: z.boolean().default(true),
  PYTH_LOG_LEVEL: z.enum(["error", "warn", "info", "debug"]).default("info"),
  RUNTIME_CHECK_MODE: z.boolean().default(false),
  PYTH_ENABLE_PRICE_STREAMING: z.boolean().default(true),
  PYTH_MAX_PRICE_STREAMS: z.string().transform(Number).default("10"),
  PYTH_TEST_ID01: z
    .string()
    .default(
      "0xe62df6c8b4a85fe1a67db44dc12de5db330f7ac66b72dc658afedf0f4a415b43"
    ),
  PYTH_TEST_ID02: z
    .string()
    .default(
      "0xff61491a931112ddf1bd8147cd1b641375f79f5825126d665480874634fd0ace"
    ),
});

export type PythConfig = z.infer<typeof pythEnvSchema>;

export function getConfig(
  env: string | undefined | null = process.env.PYTH_NETWORK_ENV || ENV
): PythConfig {
  const network = env || "mainnet";
  ENV = network;

  const maxRetries = process.env.PYTH_MAX_RETRIES
    ? Number(process.env.PYTH_MAX_RETRIES)
    : 3;
  const retryDelay = process.env.PYTH_RETRY_DELAY
    ? Number(process.env.PYTH_RETRY_DELAY)
    : 1000;
  const timeout = process.env.PYTH_TIMEOUT
    ? Number(process.env.PYTH_TIMEOUT)
    : 5000;
  const maxPriceStreams = process.env.PYTH_MAX_PRICE_STREAMS
    ? Number(process.env.PYTH_MAX_PRICE_STREAMS)
    : 10;

  return {
    PYTH_NETWORK_ENV: network as "mainnet" | "testnet",
    PYTH_MAX_RETRIES: isNaN(maxRetries) ? 3 : maxRetries,
    PYTH_RETRY_DELAY: isNaN(retryDelay) ? 1000 : retryDelay,
    PYTH_TIMEOUT: isNaN(timeout) ? 5000 : timeout,
    PYTH_GRANULAR_LOG: process.env.PYTH_GRANULAR_LOG === "true",
    PYTH_LOG_LEVEL:
      (process.env.PYTH_LOG_LEVEL as "error" | "warn" | "info" | "debug") ||
      "info",
    RUNTIME_CHECK_MODE: process.env.RUNTIME_CHECK_MODE === "true",
    PYTH_ENABLE_PRICE_STREAMING:
      process.env.PYTH_ENABLE_PRICE_STREAMING !== "false",
    PYTH_MAX_PRICE_STREAMS: isNaN(maxPriceStreams) ? 10 : maxPriceStreams,
    PYTH_TEST_ID01:
      process.env.PYTH_TEST_ID01 ||
      "0xe62df6c8b4a85fe1a67db44dc12de5db330f7ac66b72dc658afedf0f4a415b43",
    PYTH_TEST_ID02:
      process.env.PYTH_TEST_ID02 ||
      "0xff61491a931112ddf1bd8147cd1b641375f79f5825126d665480874634fd0ace",
  };
}

export async function validatePythConfig(
  runtime: IAgentRuntime
): Promise<PythConfig> {
  try {
    const networkEnv =
      runtime.getSetting("PYTH_NETWORK_ENV") ||
      process.env.PYTH_NETWORK_ENV ||
      "mainnet";
    const maxRetries =
      runtime.getSetting("PYTH_MAX_RETRIES") ||
      process.env.PYTH_MAX_RETRIES ||
      "3";
    const retryDelay =
      runtime.getSetting("PYTH_RETRY_DELAY") ||
      process.env.PYTH_RETRY_DELAY ||
      "1000";
    const logLevel =
      runtime.getSetting("PYTH_LOG_LEVEL") ||
      process.env.PYTH_LOG_LEVEL ||
      "info";
    const maxPriceStreams =
      runtime.getSetting("PYTH_MAX_PRICE_STREAMS") ||
      process.env.PYTH_MAX_PRICE_STREAMS ||
      "10";

    const config = {
      PYTH_NETWORK_ENV: networkEnv as "mainnet" | "testnet",
      PYTH_MAX_RETRIES: isNaN(Number(maxRetries)) ? 3 : Number(maxRetries),
      PYTH_RETRY_DELAY: isNaN(Number(retryDelay)) ? 1000 : Number(retryDelay),
      PYTH_TIMEOUT: 5000,
      PYTH_GRANULAR_LOG: process.env.PYTH_GRANULAR_LOG === "true",
      PYTH_LOG_LEVEL: ["error", "warn", "info", "debug"].includes(logLevel)
        ? (logLevel as "error" | "warn" | "info" | "debug")
        : "info",
      RUNTIME_CHECK_MODE: process.env.RUNTIME_CHECK_MODE === "true",
      PYTH_ENABLE_PRICE_STREAMING:
        process.env.PYTH_ENABLE_PRICE_STREAMING !== "false",
      PYTH_MAX_PRICE_STREAMS: isNaN(Number(maxPriceStreams))
        ? 10
        : Number(maxPriceStreams),
      PYTH_TEST_ID01:
        process.env.PYTH_TEST_ID01 ||
        "0xe62df6c8b4a85fe1a67db44dc12de5db330f7ac66b72dc658afedf0f4a415b43",
      PYTH_TEST_ID02:
        process.env.PYTH_TEST_ID02 ||
        "0xff61491a931112ddf1bd8147cd1b641375f79f5825126d665480874634fd0ace",
    };

    return config;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to validate Pyth configuration: ${errorMessage}`);
  }
}

// Export network configurations
export const getNetworkConfig = (env: string = ENV) => {
  const network = env === "testnet" ? "testnet" : "mainnet";
  const config = PYTH_NETWORKS[network];
  return {
    hermes: config.hermes,
    websocket: config.wss,
    rpcEndpoint: config.pythnet,
    contractAddress: config.programKey,
  };
};
